/**
 * utils/currency.ts
 * Pure utility functions for Currency domain
 *
 * Parametric by design: all functions accept data as parameters rather than
 * importing from `constants/currency`. This maintains utils purity per CODING_STANDARDS
 * and ensures functions remain testable in isolation.
 *
 * USAGE:
 * - Import currency database from `constants/currency` at call sites
 * - Pass database as function arguments
 * - Functions are pure: same inputs always produce same outputs
 *
 * @example
 * ```ts
 * // Import database from constants (at call site, not in this file)
 * import { CURRENCY_DATABASE } from '@/constants/currency';
 * import { getCurrencyDisplayName, searchCurrencies } from '@/utils/currency';
 *
 * // Pass database as argument (parametric pattern)
 * const displayName = getCurrencyDisplayName('USD', CURRENCY_DATABASE);
 * const results = searchCurrencies('dollar', CURRENCY_DATABASE);
 * ```
 *
 * ARCHITECTURAL CONSTRAINTS:
 * - Zero imports from `constants/` or other domain modules
 * - Only `import type` from `types/` (no runtime dependencies)
 * - No side effects (no logs, no mutations, no I/O, no caches)
 * - All functions are deterministic and testable
 * - **This file must not import constants** (parametric design requirement)
 */

// Internal absolute imports (type-only)
import type {
  CurrencyInfo,
  CurrencyType
} from '@/types/shared/Currency';

/**
 * Sort currencies alphabetically by code (A→Z)
 * @internal - Private helper, not exported
 */
function sortByCode(currencies: CurrencyInfo[]): CurrencyInfo[] {
  return [...currencies].sort((a, b) => a.code.localeCompare(b.code));
}

/**
 * Normalize currency code to uppercase format
 *
 * Pure normalization function: converts code to uppercase and trims whitespace.
 * Use this before any currency lookups to ensure consistent matching.
 *
 * @param code - Currency code to normalize (any case, may have whitespace)
 * @returns Normalized code (uppercase, trimmed)
 *
 * @example
 * ```ts
 * normalizeCurrencyCode('  usd  '); // => 'USD'
 * normalizeCurrencyCode('Eur');     // => 'EUR'
 * ```
 */
export function normalizeCurrencyCode(code: string): string {
  return code.toUpperCase().trim();
}

/**
 * Type guard to check if a string is a valid currency code
 *
 * Parametric validator: accepts database as argument to maintain purity.
 * Uses generic type parameter for precise type narrowing to the actual key space of the provided database.
 *
 * @param code - Currency code to validate
 * @param db - Currency database to validate against
 * @returns Type predicate narrowing code to keyof T & string
 *
 * @example
 * ```ts
 * import { CURRENCY_DATABASE } from '@/constants/currency';
 *
 * if (isValidCurrency('USD', CURRENCY_DATABASE)) {
 *   // TypeScript knows 'USD' is a valid currency code
 * }
 * ```
 */
export function isValidCurrency<T extends Record<string, CurrencyInfo>>(
  code: string,
  db: T
): code is keyof T & string {
  return normalizeCurrencyCode(code) in db;
}

/**
 * Get currency information by code
 *
 * Parametric lookup: accepts database as argument to maintain purity.
 * Normalizes the code (uppercase + trim) before lookup to handle variations.
 * Uses generic type parameter for type safety.
 *
 * @param code - Currency code to lookup (will be normalized)
 * @param db - Currency database to search
 * @returns CurrencyInfo object or undefined if not found
 *
 * @example
 * ```ts
 * import { CURRENCY_DATABASE } from '@/constants/currency';
 *
 * const info = getCurrencyInfo('USD', CURRENCY_DATABASE);
 * // => { code: 'USD', name: 'US Dollar', symbol: '$', type: 'fiat', ... }
 *
 * const missing = getCurrencyInfo('ZZZ', CURRENCY_DATABASE);
 * // => undefined
 * ```
 */
export function getCurrencyInfo<T extends Record<string, CurrencyInfo>>(
  code: string,
  db: T
): CurrencyInfo | undefined {
  return db[normalizeCurrencyCode(code)];
}

/**
 * Get currency display name
 *
 * Parametric formatter: accepts database as argument to maintain purity.
 * Falls back to the raw code if currency not found (never returns empty string).
 * Uses generic type parameter for type safety.
 *
 * **Fallback behavior**: Returns code if currency not found (never synthesizes names).
 *
 * @param code - Currency code to format
 * @param db - Currency database to lookup display name
 * @returns Display name (or code as fallback if not found)
 *
 * @example
 * ```ts
 * import { CURRENCY_DATABASE } from '@/constants/currency';
 *
 * getCurrencyDisplayName('USD', CURRENCY_DATABASE); // => 'US Dollar'
 * getCurrencyDisplayName('ZZZ', CURRENCY_DATABASE); // => 'ZZZ' (fallback)
 * ```
 */
export function getCurrencyDisplayName<T extends Record<string, CurrencyInfo>>(
  code: string,
  db: T
): string {
  const info = getCurrencyInfo(code, db);
  return info?.name ?? code;
}

/**
 * Get currency symbol
 *
 * Parametric lookup: accepts database as argument to maintain purity.
 * Returns undefined if currency not found or has no symbol (never synthesizes symbols).
 * Uses generic type parameter for type safety.
 *
 * **Never synthesizes symbols**: Returns undefined if not found, never creates fallback symbols.
 *
 * @param code - Currency code to lookup symbol for
 * @param db - Currency database to search
 * @returns Currency symbol or undefined (never synthesizes)
 *
 * @example
 * ```ts
 * import { CURRENCY_DATABASE } from '@/constants/currency';
 *
 * // Symbol found
 * getCurrencySymbol('USD', CURRENCY_DATABASE); // => '$'
 * getCurrencySymbol('BTC', CURRENCY_DATABASE); // => '₿'
 *
 * // Symbol not found - never synthesizes
 * getCurrencySymbol('ZZZ', CURRENCY_DATABASE); // => undefined (missing currency)
 * getCurrencySymbol('DAI', CURRENCY_DATABASE); // => undefined (currency exists but no symbol)
 * ```
 */
export function getCurrencySymbol<T extends Record<string, CurrencyInfo>>(
  code: string,
  db: T
): string | undefined {
  return getCurrencyInfo(code, db)?.symbol;
}

/**
 * Get currencies by type
 *
 * Parametric filter: accepts database as argument to maintain purity.
 * Uses generic type parameter for type safety.
 *
 * **Returns results in stable alphabetical order by code (A→Z)**.
 *
 * @param type - Currency type to filter by ('fiat' | 'stablecoin' | 'cryptocurrency')
 * @param db - Currency database to filter
 * @returns Array of currencies matching type, sorted alphabetically by code (A→Z)
 *
 * @example
 * ```ts
 * import { CURRENCY_DATABASE } from '@/constants/currency';
 *
 * const fiatCurrencies = getCurrenciesByType('fiat', CURRENCY_DATABASE);
 * // => [{ code: 'AED', ... }, { code: 'AUD', ... }, ...]
 * ```
 */
export function getCurrenciesByType<T extends Record<string, CurrencyInfo>>(
  type: CurrencyType,
  db: T
): CurrencyInfo[] {
  return sortByCode(Object.values(db).filter(currency => currency.type === type));
}

/**
 * Search currencies by query string
 *
 * Parametric search: accepts database as argument to maintain purity.
 * Searches code, name, and keywords (case-insensitive substring matching).
 * Empty query returns all entries.
 * Uses generic type parameter for type safety.
 *
 * **Returns results in stable alphabetical order by code (A→Z)**.
 *
 * @param query - Search query (case-insensitive, searches code/name/keywords)
 * @param db - Currency database to search
 * @returns Filtered currencies sorted alphabetically by code (A→Z)
 *
 * @example
 * ```ts
 * import { CURRENCY_DATABASE } from '@/constants/currency';
 *
 * // Empty query returns all currencies
 * const all = searchCurrencies('', CURRENCY_DATABASE);
 * // => All 78 currencies, alphabetically sorted
 *
 * // Keyword search
 * const results = searchCurrencies('dollar', CURRENCY_DATABASE);
 * // => [{ code: 'AUD', name: 'Australian Dollar', ... }, ...]
 * ```
 */
export function searchCurrencies<T extends Record<string, CurrencyInfo>>(
  query: string,
  db: T
): CurrencyInfo[] {
  if (!query.trim()) {
    return sortByCode(Object.values(db));
  }

  const searchTerm = query.toLowerCase().trim();

  return sortByCode(
    Object.values(db).filter(currency => {
      // Search in code
      if (currency.code.toLowerCase().includes(searchTerm)) {
        return true;
      }

      // Search in name
      if (currency.name.toLowerCase().includes(searchTerm)) {
        return true;
      }

      // Search in keywords (null-safe)
      if ((currency.keywords ?? []).some(keyword => keyword.toLowerCase().includes(searchTerm))) {
        return true;
      }

      return false;
    })
  );
}
